import Typography from '@mui/material/Typography';
import { Controller, useFormContext } from 'react-hook-form';
import { BaseCheckbox, Card, ControlledTextField } from '../../../../shared/components';
import ClientTypeField from '../../../components/ClientTypeField';
import OwnerContactSection from '../components/OwnerContactSection';
import POCContactSection from '../components/POCContactSection';
import { useSameAsCopy } from '../hooks/useSameAsCopy';

/**
 * "Same as X" copies the contact details only.
 *
 * Each section points at a different `/api/poc/*` master, so carrying the source's `id`
 * across would make the job reference a row in the wrong table — the copy is always a
 * new contact for the target's directory. Section-specific fields (the owner's building
 * and supervisor) are left untouched.
 *
 * The client is not a fourth contact (V36): the last section picks which of the three above
 * it is.
 */
const CONTACT_KEYS = ['name', 'phone', 'mobile', 'whatsapp', 'email', 'officeAddress'];

function contactCopy(current = {}, source = {}) {
  const copied = Object.fromEntries(CONTACT_KEYS.map((key) => [key, source[key] ?? '']));
  return { ...current, ...copied, id: null };
}

export default function StepPOC() {
  const { watch, setValue, getValues, control } = useFormContext();

  const architect = watch('architect');
  const builder = watch('builder');
  const owner = watch('owner');

  const architectDeps = [
    watch('architect.name'),
    watch('architect.phone'),
    watch('architect.mobile'),
    watch('architect.whatsapp'),
    watch('architect.email'),
    watch('architect.officeAddress'),
  ];

  const builderDeps = [
    watch('builder.name'),
    watch('builder.phone'),
    watch('builder.mobile'),
    watch('builder.whatsapp'),
    watch('builder.email'),
    watch('builder.officeAddress'),
  ];

  const ownerDeps = [
    watch('owner.name'),
    watch('owner.phone'),
    watch('owner.mobile'),
    watch('owner.whatsapp'),
    watch('owner.email'),
  ];

  // ---- Builder ← Architect ----
  const builderCopy = useSameAsCopy({
    targetDeps: builderDeps,
    sources: { ARCHITECT: architect },
    sourceDepsMap: {
      ARCHITECT: architectDeps,
    },
    onCopy: (v) =>
      setValue('builder', contactCopy(getValues('builder'), v), {
        shouldDirty: true,
        shouldValidate: false,
      }),
  });

  // ---- Owner ← Architect / Builder ----
  const ownerCopy = useSameAsCopy({
    targetDeps: ownerDeps,
    sources: {
      ARCHITECT: architect,
      BUILDER: builder,
    },
    sourceDepsMap: {
      ARCHITECT: architectDeps,
      BUILDER: builderDeps,
    },
    onCopy: (v) =>
      setValue('owner', contactCopy(getValues('owner'), v), {
        shouldDirty: true,
        shouldValidate: false,
      }),
  });

  return (
    <Card title='Point of Communication'>
      <div className='column gap-4'>
        <POCContactSection title='1. Architect Details' path='architect' />

        <POCContactSection
          title='2. Builder/Developer Details'
          path='builder'
          headerActions={
            <BaseCheckbox
              id='builder-same-as-architect'
              checked={builderCopy.activeSource === 'ARCHITECT'}
              onChange={(v) => (v ? builderCopy.copyFrom('ARCHITECT') : builderCopy.unlink())}
              label='Same as Architect'
            />
          }
        />

        <OwnerContactSection
          headerActions={
            <div className='row gap-3'>
              <BaseCheckbox
                checked={ownerCopy.activeSource === 'ARCHITECT'}
                onChange={(v) => (v ? ownerCopy.copyFrom('ARCHITECT') : ownerCopy.unlink())}
                label='Same as Architect'
              />
              <BaseCheckbox
                checked={ownerCopy.activeSource === 'BUILDER'}
                onChange={(v) => (v ? ownerCopy.copyFrom('BUILDER') : ownerCopy.unlink())}
                label='Same as Builder/Developer'
              />
            </div>
          }
        />

        <div className='column'>
          <Typography variant='body1' fontWeight={500}>
            4. Client
          </Typography>
          <Controller
            name='clientType'
            control={control}
            render={({ field }) => (
              <ClientTypeField
                value={field.value}
                onChange={field.onChange}
                names={{
                  ARCHITECT: architect?.name?.trim(),
                  BUILDER: builder?.name?.trim(),
                  OWNER: owner?.name?.trim(),
                }}
              />
            )}
          />
        </div>
      </div>

      <ControlledTextField
        id='pocNotes'
        name='pocNotes'
        label='5. Notes'
        placeholder='Add any internal notes for this step (optional)'
        isMultiline
        rows={4}
        rules={{
          maxLength: { value: 1000, message: 'Notes cannot exceed 1000 characters' },
        }}
        showCharCount
      />
    </Card>
  );
}
